import type { DocumentItem } from './model.js';
import { getValueAtPath } from './path.js';

/**
 * How much of a person to show in a cell.
 *
 * `'avatar'` pairs naturally with a tooltip, so a narrow column can still tell
 * you who someone is on hover.
 */
export type AvatarDisplay = 'avatar' | 'name' | 'both';

/** Where a value comes from: a dot path, or a function for anything else. */
export type AvatarSource = string | ((item: DocumentItem) => unknown);

export interface AvatarOptions {
  /** The display name. Defaults to the column's own key. */
  name?: AvatarSource;
  /** An image URL. Falls back to initials when absent or unloadable. */
  image?: AvatarSource;
  /** Default `'both'`. */
  display?: AvatarDisplay;
  /**
   * Show the name on hover. Defaults to `true` for `'avatar'` — an avatar with
   * no way to reveal the name is a puzzle, not a feature — and `false` when the
   * name is already on screen.
   */
  tooltip?: boolean;
  /** Override the generated colour, or derive your own from the name. */
  color?: string | ((name: string) => string);
  /** Text when there is no name at all. Default: render nothing. */
  fallback?: string;
}

export interface ResolvedAvatar {
  name: string;
  initials: string;
  imageUrl: string | undefined;
  color: string;
  /** Tooltip text, or `undefined` when the name is already visible. */
  title: string | undefined;
  showAvatar: boolean;
  showName: boolean;
}

/**
 * Six theme tokens, so avatar colours follow the palette in light and dark
 * rather than being baked in.
 */
export const AVATAR_COLOR_TOKENS = [
  'var(--de-avatar-1)',
  'var(--de-avatar-2)',
  'var(--de-avatar-3)',
  'var(--de-avatar-4)',
  'var(--de-avatar-5)',
  'var(--de-avatar-6)',
] as const;

/**
 * A stable colour per person: the same name always gets the same swatch, in
 * both frameworks and across reloads, which is what makes an avatar scannable.
 */
export function avatarColor(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = (hash * 31 + name.charCodeAt(i)) >>> 0;
  return AVATAR_COLOR_TOKENS[hash % AVATAR_COLOR_TOKENS.length] as string;
}

/**
 * Up to two initials. Handles `Ada Lovelace`, `ada.lovelace@x.com` and
 * `ada_lovelace` alike, because owner fields are rarely tidy.
 */
export function initialsOf(name: string): string {
  const cleaned = name.trim().split('@')[0] ?? '';
  const parts = cleaned.split(/[\s._-]+/).filter(Boolean);
  if (parts.length === 0) return '';
  if (parts.length === 1) return (parts[0] as string).slice(0, 2).toUpperCase();
  return `${(parts[0] as string)[0] ?? ''}${(parts[1] as string)[0] ?? ''}`.toUpperCase();
}

function read(item: DocumentItem, source: AvatarSource | undefined): unknown {
  if (source === undefined) return undefined;
  if (typeof source === 'function') return source(item);
  return getValueAtPath(item, source);
}

function asText(value: unknown): string {
  if (value == null) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return '';
}

/**
 * Resolve everything a renderer needs to draw one avatar cell.
 *
 * Framework-free on purpose: React and Angular both call this, so an avatar
 * looks and behaves the same in either, down to the colour.
 *
 * Returns `null` when there is nothing to show and no fallback, so the cell can
 * render empty rather than an anonymous grey circle for every row.
 */
export function resolveAvatar(
  item: DocumentItem,
  columnKey: string,
  options: AvatarOptions = {},
): ResolvedAvatar | null {
  const name = asText(read(item, options.name ?? columnKey)) || (options.fallback ?? '');
  const imageUrl = asText(read(item, options.image)) || undefined;

  if (name === '' && imageUrl === undefined) return null;

  const display = options.display ?? 'both';
  const showAvatar = display !== 'name';
  const showName = display !== 'avatar';

  // An avatar-only cell needs a way to reveal the name; a cell already showing
  // the name does not need to repeat it.
  const tooltip = options.tooltip ?? !showName;

  const color =
    typeof options.color === 'function'
      ? options.color(name)
      : options.color ?? avatarColor(name);

  return {
    name,
    initials: initialsOf(name),
    imageUrl,
    color,
    title: tooltip && name !== '' ? name : undefined,
    showAvatar,
    showName,
  };
}
